import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { reportErrorBoundary } from "@/lib/error-reporter";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    // Report to backend monitoring
    reportErrorBoundary(error, info.componentStack ?? undefined);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-4 bg-white px-6 dark:bg-s0">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-950/40">
            <AlertTriangle className="h-7 w-7 text-red-500 dark:text-red-400" />
          </div>
          <h2 className="text-lg font-bold text-t0">
            Something went wrong
          </h2>
          <p className="max-w-md text-center text-sm text-t3">
            {this.state.error?.message || "An unexpected error occurred"}
          </p>
          <div className="mt-2 flex gap-3">
            <button
              onClick={this.handleReset}
              className="rounded-lg border border-b0 px-4 py-2 text-sm font-medium text-t2 transition-colors hover:bg-s2 dark:border-b0 dark:text-t1 dark:hover:bg-s2"
            >
              Retry
            </button>
            <button
              onClick={this.handleReload}
              className="flex items-center gap-1.5 rounded-lg bg-s1 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-s2 dark:bg-s2 dark:text-t0 dark:hover:bg-s2"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
